import { usePortalQuery } from "@/state/hooks/usePortalQuery";
import { queryClient } from "@/state/queryClient";
import { queryKeys } from "@/state/queryKeys";
import trpc from "@/shared/utils/trpc";

/** Shared badge defaults: long stale window, no focus spam, soft poll. */
const BADGE_STALE_MS = 60_000;
const BADGE_POLL_MS = 90_000;

export function usePendingRequestCountsQuery(enabled: boolean) {
  return usePortalQuery({
    queryKey: queryKeys.badges.pendingRequests(),
    enabled,
    staleTime: BADGE_STALE_MS,
    refetchInterval: enabled ? BADGE_POLL_MS : false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async () => {
      try {
        const res = await trpc.collaboration.getPendingRequestCounts.query();
        return {
          received: res.received ?? 0,
          sent: res.sent ?? 0,
          upcomingMeetingCount: res.upcomingMeetings ?? 0,
        };
      } catch (err) {
        console.error("Error fetching pending request counts:", err);
        return { received: 0, sent: 0, upcomingMeetingCount: 0 };
      }
    },
  });
}

export function useCollaborationSidebarCountsQuery(enabled: boolean) {
  return usePortalQuery({
    queryKey: queryKeys.badges.collaborationSidebar(),
    enabled,
    staleTime: BADGE_STALE_MS,
    refetchInterval: enabled ? BADGE_POLL_MS : false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async () => {
      try {
        const res = await trpc.collaboration.getCollaborationSidebarCounts.query();
        return { active: res.active, closed: res.closed };
      } catch (err) {
        console.error("Error fetching collaboration sidebar counts:", err);
        return { active: 0, closed: 0 };
      }
    },
  });
}

export function useCollaborationBalanceQuery(enabled: boolean) {
  return usePortalQuery({
    queryKey: queryKeys.badges.wallet(),
    enabled,
    staleTime: BADGE_STALE_MS,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async () => {
      try {
        const res = await trpc.wallet.getBalance.query();
        return { balance: res.available as number, failed: false };
      } catch (err) {
        console.error("Error fetching collaboration balance:", err);
        return { balance: null as number | null, failed: true };
      }
    },
  });
}

export function invalidatePendingRequestCounts() {
  return queryClient.invalidateQueries({ queryKey: queryKeys.badges.pendingRequests() });
}

export function invalidateCollaborationSidebarCounts() {
  return queryClient.invalidateQueries({ queryKey: queryKeys.badges.collaborationSidebar() });
}

export function invalidateCollaborationBalance() {
  return queryClient.invalidateQueries({ queryKey: queryKeys.badges.wallet() });
}

export function clearBadgeQueries() {
  return queryClient.removeQueries({ queryKey: queryKeys.badges.all });
}
